import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import './OpsNfps.css';

const SAVED_VIEWS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'live_nfps', label: 'Live NFPs', params: { visibilityState: 'visible', visibilityReady: 'true' } },
  { key: 'proof_queue', label: 'Proof Review Queue', params: { queue: 'proof_pending_review' } },
  { key: 'moderation_queue', label: 'Moderation Queue', params: { queue: 'moderation_pending' } },
  { key: 'profile_review', label: 'Profile Review', params: { queue: 'profile_review' } },
  { key: 'geography_review', label: 'Geography Review', params: { queue: 'geography_review' } },
  { key: 'hidden', label: 'Hidden (Visibility Blocked)', params: { queue: 'hidden' } },
  { key: 'visible_payout_not_ready', label: 'Visible but Payout Not Ready', params: { queue: 'visible_payout_not_ready' } },
  { key: 'needs_action', label: 'Needs Action', params: { view: 'needs_action' } },
  { key: 'recent', label: 'Recently Updated', params: { view: 'recently_updated' } },
];

const EMPTY_FILTERS = {
  sortBy: 'updated_desc',
  registrationStatus: '',
  proofStatus: '',
  moderationStatus: '',
  status: '',
  visibilityState: '',
  visibilityReady: '',
  payoutReady: '',
  hiddenReasonContains: '',
  area: '',
  submittedFrom: '',
  submittedTo: '',
  updatedFrom: '',
  updatedTo: '',
};

function toSearchParams(base = {}) {
  const params = new URLSearchParams();
  Object.entries(base).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value) !== '') params.set(key, String(value));
  });
  return params;
}

function titleCaseWords(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatCauseLabel(code, note) {
  if (!code) return '—';
  const label = titleCaseWords(code);
  return note ? `${label}: ${note}` : label;
}

const NFP_BLOCKER_LABELS = {
  nfp_not_active: 'NFP Not Active',
  content_restricted: 'Content Restricted',
  proof_rejected: 'Proof Rejected',
  connected_account_missing: 'Connected Account Missing',
  connected_account_incomplete: 'Connected Account Incomplete',
  transfers_not_enabled: 'Transfers Not Enabled',
  payouts_paused: 'Payouts Paused',
  recent_payout_failed: 'Recent Payout Failed',
  geography_review: 'Geography Review',
  root_geography_unresolved: 'Root Geography Not Verified',
  service_area_unresolved: 'Service Area Unresolved',
};

function formatNfpBlockerCode(code) {
  return NFP_BLOCKER_LABELS[code] || titleCaseWords(code || '');
}

function deriveNfpBlockers(row = {}) {
  const live = Array.isArray(row?.blockers?.live) ? row.blockers.live : [];
  const payout = Array.isArray(row?.blockers?.payout) ? row.blockers.payout : [];
  const geography = row?.geography || {};
  const geographyNeedsReview = !!row?.queueFlags?.geographyNeedsReview || !!geography?.hasUnresolved;
  const geographyBlockers = [];
  if (geographyNeedsReview) {
    geographyBlockers.push({
      track: 'geography',
      code: 'geography_review',
      severity: 'High',
      causeCode: geography?.reasonCode || (geography?.rootGeographyUnresolved ? 'root_geography_unresolved' : 'service_area_unresolved'),
      causeNote: geography?.rootGeographyUnresolved
        ? 'Root/HQ market needs manual confirmation'
        : 'Service-area coverage needs Ops resolution',
    });
  }
  const combined = [...geographyBlockers, ...live, ...payout];
  const seen = new Set();
  return combined.filter((blocker) => {
    const key = `${blocker?.track || ''}:${blocker?.code || ''}`;
    if (!blocker?.code || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function nfpActionForBlocker(code) {
  if (code === 'geography_review' || code === 'root_geography_unresolved' || code === 'service_area_unresolved') return 'geography';
  if (code === 'nfp_not_active') return 'causeStatus';
  if (code === 'content_restricted') return 'moderation';
  if (code === 'proof_rejected') return 'proof';
  return '';
}

function getDirectNfpBlockerAction(blocker = {}) {
  const direct = (Array.isArray(blocker?.actions) ? blocker.actions : []).find((action) => action?.opsCanExecute && action?.key);
  if (direct?.key) return direct.key;
  return nfpActionForBlocker(blocker?.code || '');
}

export default function OpsNfpMonitoring() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ areas: [] });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeView, setActiveView] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const limit = 50;

  useEffect(() => {
    const incoming = { ...EMPTY_FILTERS };
    Object.keys(EMPTY_FILTERS).forEach((key) => {
      const v = searchParams.get(key);
      if (v != null) incoming[key] = v;
    });
    setFilters(incoming);
    const hasActiveFilter = Object.entries(incoming).some(([k, value]) => k !== 'sortBy' && String(value || '').trim() !== '');
    setFiltersOpen(hasActiveFilter);
    const view = searchParams.get('savedView');
    if (view && SAVED_VIEWS.some((x) => x.key === view)) setActiveView(view);
    const pageRaw = Number(searchParams.get('page'));
    setPage(Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams(searchParams);
        params.set('limit', String(limit));
        params.set('page', String(page));
        const data = await apiFetch(`/ops/nfps?${params.toString()}`);
        if (cancelled) return;
        setRows(Array.isArray(data?.nfps) ? data.nfps : []);
        setTotal(Number(data?.total || 0));
        setFilterOptions({
          areas: Array.isArray(data?.filters?.areas) ? data.filters.areas : [],
        });
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401 || err?.status === 403) {
          clearAuthToken();
          navigate('/login', { replace: true });
          return;
        }
        setError(err?.data?.error || err?.message || 'Failed to load NFP monitoring list');
        setRows([]);
        setTotal(0);
        setFilterOptions({ areas: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [navigate, page, searchParams]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const activeViewLabel = useMemo(() => SAVED_VIEWS.find((x) => x.key === activeView)?.label || 'All', [activeView]);

  function applySavedView(viewKey) {
    const view = SAVED_VIEWS.find((x) => x.key === viewKey) || SAVED_VIEWS[0];
    setActiveView(view.key);
    const params = { savedView: view.key, page: 1, ...view.params };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    setSearchParams(toSearchParams(params));
  }

  function applyFilters() {
    const params = { savedView: activeView, page: 1 };
    const viewParams = SAVED_VIEWS.find((x) => x.key === activeView)?.params || {};
    Object.assign(params, viewParams);
    Object.entries(filters).forEach(([k, v]) => {
      if (String(v || '') !== '') params[k] = v;
    });
    setSearchParams(toSearchParams(params));
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    const viewParams = SAVED_VIEWS.find((x) => x.key === activeView)?.params || {};
    setSearchParams(toSearchParams({ savedView: activeView, page: 1, ...viewParams }));
  }

  return (
    <OpsLayout title="NFP Monitoring" subtitle="Triage and review queue for cause/NFP readiness and visibility.">
      <div className="ops-nfp-content">

      <section className="ops-nfp-tabs">
        {SAVED_VIEWS.map((view) => (
          <button
            key={view.key}
            type="button"
            className={`ops-nfp-tab ${activeView === view.key ? 'active' : ''}`}
            onClick={() => applySavedView(view.key)}
          >
            {view.label}
          </button>
        ))}
      </section>

      <section className="ops-nfp-panel">
        <div className="ops-nfp-panel-header">
          <h3 className="ops-section-title">Filters</h3>
          <button
            type="button"
            className="ops-btn"
            onClick={() => setFiltersOpen((v) => !v)}
            aria-expanded={filtersOpen}
          >
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
        {filtersOpen && (
          <div className="ops-nfp-panel-body">
            <div className="ops-nfp-grid">
              <div className="ops-nfp-field">
                <label>Registration Status</label>
                <select value={filters.registrationStatus} onChange={(e) => setFilters((p) => ({ ...p, registrationStatus: e.target.value }))}>
                  <option value="">All</option>
                  <option value="draft">draft</option>
                  <option value="submitted">submitted</option>
                  <option value="needs_action">needs_action</option>
                  <option value="under_review">under_review</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                  <option value="withdrawn">withdrawn</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Proof Status</label>
                <select value={filters.proofStatus} onChange={(e) => setFilters((p) => ({ ...p, proofStatus: e.target.value }))}>
                  <option value="">All</option>
                  <option value="unsubmitted">unsubmitted</option>
                  <option value="pending_review">pending_review</option>
                  <option value="verified">verified</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Moderation Status</label>
                <select value={filters.moderationStatus} onChange={(e) => setFilters((p) => ({ ...p, moderationStatus: e.target.value }))}>
                  <option value="">All</option>
                  <option value="not_submitted">not_submitted</option>
                  <option value="pending">pending</option>
                  <option value="approved">approved</option>
                  <option value="needs_action">needs_action</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Cause Status</label>
                <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
                  <option value="">All</option>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Visibility State</label>
                <select value={filters.visibilityState} onChange={(e) => setFilters((p) => ({ ...p, visibilityState: e.target.value }))}>
                  <option value="">All</option>
                  <option value="visible">visible</option>
                  <option value="hidden">hidden</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Visibility Ready</label>
                <select value={filters.visibilityReady} onChange={(e) => setFilters((p) => ({ ...p, visibilityReady: e.target.value }))}>
                  <option value="">All</option>
                  <option value="true">yes</option>
                  <option value="false">no</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Payout Ready</label>
                <select value={filters.payoutReady} onChange={(e) => setFilters((p) => ({ ...p, payoutReady: e.target.value }))}>
                  <option value="">All</option>
                  <option value="true">yes</option>
                  <option value="false">no</option>
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Hidden Reason Contains</label>
                <input value={filters.hiddenReasonContains} onChange={(e) => setFilters((p) => ({ ...p, hiddenReasonContains: e.target.value }))} />
              </div>
              <div className="ops-nfp-field">
                <label>Area Served</label>
                <select value={filters.area} onChange={(e) => setFilters((p) => ({ ...p, area: e.target.value }))}>
                  <option value="">All</option>
                  {(Array.isArray(filterOptions.areas) ? filterOptions.areas : []).map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="ops-nfp-field">
                <label>Submitted From</label>
                <input type="date" value={filters.submittedFrom} onChange={(e) => setFilters((p) => ({ ...p, submittedFrom: e.target.value }))} />
              </div>
              <div className="ops-nfp-field">
                <label>Submitted To</label>
                <input type="date" value={filters.submittedTo} onChange={(e) => setFilters((p) => ({ ...p, submittedTo: e.target.value }))} />
              </div>
              <div className="ops-nfp-field">
                <label>Updated From</label>
                <input type="date" value={filters.updatedFrom} onChange={(e) => setFilters((p) => ({ ...p, updatedFrom: e.target.value }))} />
              </div>
              <div className="ops-nfp-field">
                <label>Updated To</label>
                <input type="date" value={filters.updatedTo} onChange={(e) => setFilters((p) => ({ ...p, updatedTo: e.target.value }))} />
              </div>
            </div>
            <div className="ops-nfp-filter-actions">
              <button type="button" className="ops-btn primary" onClick={applyFilters}>Apply Filters</button>
              <button type="button" className="ops-btn" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        )}
      </section>

      <section className="ops-nfp-panel">
        <div className="ops-nfp-panel-header">
          <h3 className="ops-section-title">{activeViewLabel}</h3>
          <div className="ops-nfp-list-controls">
            <label htmlFor="nfp-sort-select">Sort</label>
            <select
              id="nfp-sort-select"
              value={filters.sortBy}
              onChange={(e) => {
                const nextSort = e.target.value;
                setFilters((p) => ({ ...p, sortBy: nextSort }));
                const p = new URLSearchParams(searchParams);
                p.set('sortBy', nextSort);
                p.set('page', '1');
                setSearchParams(p);
              }}
            >
              <option value="updated_desc">updated_desc (default)</option>
              <option value="updated_asc">updated_asc</option>
              <option value="queue_age_desc">queue_age_desc</option>
              <option value="queue_age_asc">queue_age_asc</option>
              <option value="pending_age_asc">pending_age_asc</option>
            </select>
          </div>
        </div>
        {loading && <div>Loading NFP queue…</div>}
        {!loading && error && <div>{error}</div>}
        {!loading && !error && (
          <div className="ops-nfp-table-wrap">
            <table className="ops-nfp-table">
              <thead>
                <tr>
                  <th>NFP Name</th>
                  <th title="Change registration status in NFP detail.">Registration</th>
                  <th title="Change proof status in NFP detail.">Proof</th>
                  <th title="Change moderation status in NFP detail.">Moderation</th>
                  <th title="Review new NFP profile text or image changes waiting for Ops.">Profile Review</th>
                  <th title="Change cause status in NFP detail.">Cause Status</th>
                  <th title="Root and service-area geography resolution state.">Geography</th>
                  <th title="Review consumer visibility.">Visibility</th>
                  <th>Live Ready</th>
                  <th>Payout Readiness</th>
                  <th>Payout Block Reason</th>
                  <th>Last Updated</th>
                  <th>Updated By</th>
                  <th>Blockers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={15}>No NFPs matched this view/filter.</td>
                  </tr>
                )}
                {rows.map((row) => (
                  <tr key={row.id || row.causeId}>
                    <td>{row.name || row.causeId || 'Unknown'}</td>
                    <td><span className="ops-chip">{row.registrationStatus || '—'}</span></td>
                    <td><span className="ops-chip">{row.proofVerificationStatus || '—'}</span></td>
                    <td><span className="ops-chip">{row.moderationStatus || '—'}</span></td>
                    <td>
                      {row.queueFlags?.profileReviewNeeded ? (
                        <button
                          type="button"
                          className="ops-chip bad"
                          style={{ border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                          title="New NFP profile text or image changes are waiting for Ops review. Open detail to compare Current Live, Ops Draft, and Proposed (NFP)."
                          onClick={() => navigate(`/ops/nfps/${row.id || row.causeId}`)}
                        >
                          Needs Review
                        </button>
                      ) : (
                        <span className="ops-chip good" title="No unpublished NFP profile changes are waiting for review.">
                          Up to date
                        </span>
                      )}
                    </td>
                    <td><span className={`ops-chip ${row.status === 'active' ? 'good' : 'bad'}`}>{row.status || '—'}</span></td>
                    <td>
                      {row.queueFlags?.geographyNeedsReview || row.geography?.hasUnresolved ? (
                        <button
                          type="button"
                          className="ops-chip bad"
                          style={{ border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                          title={`${row.geography?.rootGeographyUnresolved ? 'Root/HQ market exists but may not be verified. ' : ''}${row.geography?.serviceAreaUnresolved ? 'Service area needs review. ' : ''}Open detail to retry, confirm root market, or mark out-of-footprint.`}
                          onClick={() => navigate(`/ops/nfps/${row.id || row.causeId}?action=geography`)}
                        >
                          Geography Review
                        </button>
                      ) : (
                        <span className="ops-chip good">Resolved</span>
                      )}
                    </td>
                    <td><span className={`ops-chip ${row.visibility?.state === 'visible' ? 'good' : 'bad'}`}>{row.visibility?.state || 'hidden'}</span></td>
                    <td>{row.readiness?.visibilityReady ? 'yes' : 'no'}</td>
                    <td>{row.payoutReadinessStatus || '—'}</td>
                    <td>{row.payoutBlockedReason || '—'}</td>
                    <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                    <td>{row.updatedBy || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {deriveNfpBlockers(row).length === 0 ? (
                          <span className="ops-chip good">None</span>
                        ) : deriveNfpBlockers(row).slice(0, 3).map((blocker) => {
                          const action = getDirectNfpBlockerAction(blocker);
                          const params = new URLSearchParams();
                          if (action) params.set('action', action);
                          const href = `/ops/nfps/${row.id || row.causeId}${params.toString() ? `?${params.toString()}` : ''}`;
                          const title = `${String(blocker.track || 'nfp').toUpperCase()} blocker: ${formatNfpBlockerCode(blocker.code)}${blocker.causeCode ? ` - ${formatCauseLabel(blocker.causeCode, blocker.causeNote)}` : ''}${action ? ' - Open the matching action in detail.' : ''}`;
                          if (!action) {
                            return (
                              <span
                                key={`${blocker.track}:${blocker.code}`}
                                className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                                style={{ fontSize: 12 }}
                                title={title}
                              >
                                {formatNfpBlockerCode(blocker.code)}
                              </span>
                            );
                          }
                          return (
                            <button
                              key={`${blocker.track}:${blocker.code}`}
                              type="button"
                              className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                              style={{ fontSize: 12, border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                              title={title}
                              onClick={() => navigate(href)}
                            >
                              {formatNfpBlockerCode(blocker.code)}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="ops-btn"
                        title="Open NFP detail."
                        onClick={() => navigate(`/ops/nfps/${row.id || row.causeId}`)}
                      >
                        Open detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="ops-nfp-filter-actions">
          <button
            type="button"
            className="ops-btn"
            disabled={page <= 1}
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.set('page', String(Math.max(1, page - 1)));
              setSearchParams(p);
            }}
          >
            Prev
          </button>
          <div style={{ alignSelf: 'center', color: '#45676d' }}>
            Page {page} / {totalPages} ({total.toLocaleString('en-US')} total)
          </div>
          <button
            type="button"
            className="ops-btn"
            disabled={page >= totalPages}
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.set('page', String(Math.min(totalPages, page + 1)));
              setSearchParams(p);
            }}
          >
            Next
          </button>
        </div>
      </section>
      </div>
    </OpsLayout>
  );
}
